import { Box, Divider, Stack, Typography } from '@mui/material';
import { FormEventHandler } from 'react';
import { useFormItem } from '../../../common/hooks/use-form-item';
import { InputType } from '../../../common/hooks/use-form-item/types';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../../common/hooks/use-form-item/validators/operational';
import { DangerButton, PrimaryButton } from '../../../modal-flows/components/Buttons';
import { ACCORDION_STYLES } from '../../../common/components/IntegrationAccordion';
import { ServiceNowIntegration } from '../../../integrations/service-now/models/ServiceNowIntegration';
import { CheckServiceNowConnectionArgs, UpdateServiceNowArgs } from '../../../integrations/service-now/services/ServiceNowRepository';
import { t } from 'i18next';
import { url, urlWithoutTrailingSlash } from '../../../common/hooks/use-form-item/validators/type';

export type ServiceNowAccordionFormProps = {
  model: ServiceNowIntegration | null;
  onTestClick: (args: CheckServiceNowConnectionArgs) => void;
  onConnectClick: (args: UpdateServiceNowArgs) => void;
  onUnlinkClick: () => void;
  canEdit: boolean;
};

const MAX_INSTANCE_URL_LENGTH = 2000;
const MAX_USER_ID_LENGTH = 40;
const MAX_PASSWORD_LENGTH = 100;

export const ServiceNowAccordionForm = (props: ServiceNowAccordionFormProps) => {
  const { model, onConnectClick, onTestClick, canEdit, onUnlinkClick } = props;
  const isConfigured = !!model;

  const [instanceUrl, instanceUrlComponent, isInstanceUrlValid] = useFormItem({
    type: InputType.TYPE_TEXT,
    value: model?.getInstanceUrl() ?? '',
    inputOptions: {
      autoFocus: true,
      disabled: !canEdit,
      showRequiredIndicator: true,
    },
    validator: and(required(), url(), urlWithoutTrailingSlash(), maxLength(MAX_INSTANCE_URL_LENGTH)),
    label: t('integrationList.serviceNow.instanceUrlLabel'),
  });

  const [userId, userIdComponent, isUserIdValid] = useFormItem({
    type: InputType.TYPE_TEXT,
    value: model?.getUserID() ?? '',
    inputOptions: {
      autoFocus: true,
      disabled: !canEdit,
      showRequiredIndicator: true,
    },
    validator: and(required(), maxLength(MAX_USER_ID_LENGTH)),
    label: t('integrationList.serviceNow.userIDLabel'),
  });

  const [password, passwordComponent, isPasswordValid] = useFormItem({
    type: InputType.TYPE_PASSWORD,
    value: '',
    inputOptions: {
      // Prevent browsers from trying to prefill the user's password in this field:
      // `one-time-code` seems to be the least unrelated value for a token, see
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
      autoComplete: 'one-time-code',
      disabled: !canEdit,
      showRequiredIndicator: true,
    },
    validator: and(required(), maxLength(MAX_PASSWORD_LENGTH)),
    label: t('integrationList.serviceNow.passwordLabel'),
  });

  const isFormValid = isInstanceUrlValid && isUserIdValid && isPasswordValid;
  // We never get a password back from the integration model so we can't check if it's updated or not
  const fieldsModified = instanceUrl !== model?.getInstanceUrl() || userId !== model?.getUserID();
  const canSubmit = isFormValid && fieldsModified;

  const handleConnectButtonClick: FormEventHandler = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }

    onConnectClick({
      instanceUrl,
      userId,
      password,
    });
  };

  const handleTestClick = () => {
    if (fieldsModified && !isFormValid) {
      return;
    }

    onTestClick({
      instanceUrl,
      userId,
      password,
    });
  };

  return (
    <Box sx={{ p: 3, backgroundImage: ACCORDION_STYLES.backgroundImage }}>
      <Stack spacing={1.5} component="form" onSubmit={handleConnectButtonClick}>
        {instanceUrlComponent}
        {userIdComponent}
        {passwordComponent}
        <Divider />
        <Stack direction="row" gap={2}>
          {canEdit ? (
            <>
              {isConfigured && <DangerButton onClick={onUnlinkClick}>{t('integrationList.serviceNow.unlinkButton')}</DangerButton>}
              <PrimaryButton disabled={fieldsModified && !isFormValid} onClick={handleTestClick} sx={{ ml: 'auto' }}>
                {t('integrationList.serviceNow.testButton')}
              </PrimaryButton>
              <PrimaryButton disabled={!canSubmit} type="submit">
                {t('integrationList.serviceNow.connectButton')}
              </PrimaryButton>
            </>
          ) : (
            <Typography>{t('integrationList.noPermission')}</Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
